import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PhoneNumberField, PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import useConstructor from "@preferredMedical/Components/Misc/UseConstructor";
import { BackOrderExpediteEmailRequest } from "@preferredMedical/Models/CustomForms/BackOrderRequestExpedite/BackOrderExpediteEmailRequest";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { sendBackOrderExpediteEmail } from "@preferredMedical/Services/CustomFormsService";
import { removeSitePrefix } from "@preferredMedical/Store/Data/Products/ProductsSelectors";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order: Readonly<OrderModel>;
    onModalClose: () => void;
}
const mapStateToProps = (state: ApplicationState) => ({
    sitePrefix: getSettingsCollection(state).productNumberPrefix,
    userName: state.context.session.isAuthenticated ? `${state.context.session.userName}` : "",
    contactName: state.context.session.isAuthenticated
        ? `${state.context.session.firstName} ${state.context.session.lastName}`
        : "",
    emailRecipients: state.context.session.isAuthenticated ? `${state.context.session.email}` : "",
});
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BackOrderRequestExpediteStyles {
    container?: GridContainerProps;
    containerResponsive?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    threeLinerGridItem?: GridItemProps;
    fourLinerGridItem?: GridItemProps;
    sixLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    submitButtonWrapper?: InjectableCss;
    addButton?: ButtonPresentationProps;
    inlineCheckbox?: InjectableCss;
    columnView?: InjectableCss;
    formSecondaryTitle?: InjectableCss;
    verticalSpacing?: InjectableCss;
    addButtonWrapper?: InjectableCss;
    mainWrapper?: GridContainerProps;
}

export interface BackOrderRequestExpediteFormData {
    name: string;
    email: string;
    orderNumber: string;
    accountID: string;
    customerCode: string;
    contactName: string;
    phone: string;
    productBackOrderErpNumber: string[];
    comments: string;

    contactNameError: string;
    contactEmailError: string;
    nameError: string;
    accountIdError: string;
    phoneNumberError: string;

    contactNumberOnChange: EventEx;
    contactNumberUpdate: EventEx;
}

export const styles: BackOrderRequestExpediteStyles = {
    container: {
        gap: 10,
        css: css`
            text-align: left;
            margin-bottom: 20px;
        `,
    },
    containerResponsive: {
        gap: 10,
        css: css`
            text-align: left;
            margin-bottom: 20px;
            padding-left: 5px;
            padding-right: 5px;
            @media (max-width: 991px) {
                div[class*="GridItemStyle"] {
                    max-width: 50%;
                    flex-basis: 50%;
                }
            }
        `,
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding: 5px;
        `,
    },
    threeLinerGridItem: {
        width: 4,
        css: css`
            padding: 5px;
        `,
    },
    fourLinerGridItem: {
        width: 3,
        css: css`
            padding: 5px;
        `,
    },
    sixLinerGridItem: {
        width: 2,
        css: css`
            padding: 5px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
    addButton: {
        variant: "secondary",
        css: css`
            text-align: center;
        `,
    },
    inlineCheckbox: {
        css: css`
            display: flex;
            flex-direction: column;
            div[class*="Checkbox"] {
                margin-left: 10px;

                span {
                    + label {
                        margin-left: 5px;
                    }
                }

                + div[class*="Checkbox"] {
                    margin-top: 0;
                }
            }
        `,
    },
    columnView: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    formSecondaryTitle: {
        css: css`
            border-top: 1px solid #cccccc;
            padding-top: 20px;
            margin-top: 30px;
            margin-bottom: 30px;
            width: 100%;
            text-align: center;
            font-size: 20px;
            font-weight: 700;
        `,
    },
    verticalSpacing: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
        `,
    },
    mainWrapper: {
        css: css`
            width: 100%;
            margin: 0;
            padding-left: 15px;
            padding-right: 15px;
        `,
    },
    addButtonWrapper: {
        css: css`
            border-bottom: 1px solid #ccc;
            margin-bottom: 30px;
            padding-bottom: 30px;
            width: 100%;
            text-align: center;
        `,
    },
    submitButtonWrapper: {
        css: css`
            min-width: 320px;
        `,
    },
};

export interface BackOrderRequestExpediteState {
    inProgress: boolean;
    formData: BackOrderRequestExpediteFormData;
}

const BackOrderRequestExpediteForm = ({
    contactName,
    userName,
    order,
    sitePrefix,
    emailRecipients,
    onModalClose,
}: Props) => {
    const filteredBackorderedLines = order.orderLines?.filter(
        orderLine =>
            "backOrderedProduct" in orderLine.properties &&
            orderLine.properties["backOrderedProduct"] === orderLine.productErpNumber,
    );

    const [inProgress, setInProgress] = useState(false);

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [emailState, setEmailState] = useState(emailRecipients);
    const [emailStateError, setEmailStateError] = useState("");
    const [accountID, setAccountID] = useState(userName);
    const [accountIDError, setAccountIDError] = useState("");
    const [customerCode, setCustomerCode] = useState(order.customerNumber);
    const [contactNameState, setContactNameState] = useState(contactName);
    const [contactNameStateError, setContactNameStateError] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [orderNumber, setOrderNumber] = useState(order.erpOrderNumber);
    const [productBackOrderErpNumber, setProductBackOrderErpNumber] = useState(
        filteredBackorderedLines
            ? filteredBackorderedLines.map(a => removeSitePrefix(sitePrefix, a.productErpNumber))
            : [""],
    );
    const [comments, setComments] = useState("");

    const [phoneOnChange, setPhoneOnChange] = React.useState(new EventEx());
    const [phoneUpdate, setPhoneUpdate] = React.useState(new EventEx());

    const createModel = () => {
        const model = new BackOrderExpediteEmailRequest();
        model.name = name;
        model.email = emailState;
        model.accountID = accountID;
        model.customerCode = customerCode;
        model.contactName = contactNameState;
        model.phone = phone;
        model.orderNumber = orderNumber;
        model.productBackOrderErpNumber = productBackOrderErpNumber;
        model.comments = comments;
        return model;
    };

    const submitButtonClickHandler = async (addToast: Function) => {
        const hasErrors = await checkForErrors();

        if (hasErrors) {
            return;
        }

        setInProgress(true);

        const model = createModel();

        sendBackOrderExpediteEmail(model).then(x => {
            if (x.successful === true) {
                const successToast = {
                    body: `Request expedite for order #${order.erpOrderNumber} was successfully sent.`,
                    messageType: "info",
                };
                addToast(successToast);
                setTimeout(() => onModalClose(), 3000);
            } else {
                const errorToast = {
                    body: x.errorMessage,
                    messageType: "error",
                };
                addToast(errorToast);
            }
        });
    };

    const checkForErrors = async () => {
        const requiredFieldMessage = translate("This is a required field.");
        const emailFieldMessage = translate("Enter a valid email address.");
        const emailRegex = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
        let encounteredError = false;

        if (!emailState || emailState === "" || !emailRegex.test(emailState)) {
            if (!emailState || emailState === "") {
                setEmailStateError(requiredFieldMessage);
            }
            if (!emailRegex.test(emailState)) {
                setEmailStateError(emailFieldMessage);
            }
            encounteredError = true;
        } else {
            setEmailStateError("");
        }

        if (!contactNameState || contactNameState === "") {
            setContactNameStateError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setContactNameStateError("");
        }

        if (!name || name === "") {
            setNameError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setNameError("");
        }

        if (!accountID || accountID === "") {
            setAccountIDError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setAccountIDError("");
        }

        if (!phone || phone === "") {
            setPhoneError(requiredFieldMessage);
            encounteredError = true;
        }

        await phoneUpdate.emitAsync();
        if (phoneError) {
            encounteredError = true;
        }

        return encounteredError;
    };

    useConstructor(() => {
        phoneOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await setPhone(phoneNumberState.value);
            await setPhoneError(phoneNumberState.error);
        });
    });

    return (
        <Toaster>
            <ToasterContext.Consumer>
                {({ addToast }) => (
                    <StyledWrapper {...styles.mainWrapper}>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.oneLinerGridItem}>
                                <b>
                                    Expedite requests are not guaranteed. Shipping method provided at order checkout
                                    will not be adjusted unless specifically requested.
                                </b>
                            </GridItem>
                            <GridItem {...styles.oneLinerGridItem}>
                                <TextField
                                    label={translate("Name")}
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    data-test-selector="name"
                                    error={nameError}
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <TextField
                                    label={translate("Email")}
                                    value={emailState}
                                    onChange={e => setEmailState(e.target.value)}
                                    error={emailStateError}
                                    data-test-selector="email"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <TextField
                                    label={translate("Accound ID")}
                                    value={accountID}
                                    onChange={e => setAccountID(e.target.value)}
                                    data-test-selector="accountId"
                                    error={accountIDError}
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <TextField
                                    label={translate("Customer Code")}
                                    value={order.customerNumber}
                                    disabled={true}
                                    data-test-selector="customerCode"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <TextField
                                    label={translate("Contact Name")}
                                    value={contactNameState}
                                    onChange={e => setContactNameState(e.target.value)}
                                    data-test-selector="contactName"
                                    error={contactNameStateError}
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <PhoneNumberField
                                    label={translate("Phone")}
                                    initialValue={phone}
                                    onChange={phoneOnChange}
                                    update={phoneUpdate}
                                    data-test-selector="phone"
                                    format="000-000-0000"
                                    required={true}
                                    hideRequiredAsterisk={false}
                                />
                            </GridItem>
                            <GridItem {...styles.twoLinerGridItem}>
                                <TextField
                                    label={translate("Order Number")}
                                    value={order.erpOrderNumber}
                                    disabled={true}
                                    data-test-selector="orderNumber"
                                    required={true}
                                />
                            </GridItem>
                            {productBackOrderErpNumber &&
                                productBackOrderErpNumber.map((productNumber, index) => {
                                    return (
                                        <GridContainer {...styles.containerResponsive} key={productNumber}>
                                            <GridItem {...styles.fourLinerGridItem}>
                                                <StyledWrapper {...styles.columnView}>
                                                    <TextField
                                                        label={translate(`Part Number ${index + 1}`)}
                                                        required={true}
                                                        disabled={true}
                                                        value={removeSitePrefix(sitePrefix, productNumber)}
                                                        data-test-selector="productBackOrderErpNumber"
                                                    />
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    );
                                })}
                            <GridItem {...styles.oneLinerGridItem}>
                                <TextArea
                                    label={translate("Comments")}
                                    value={comments}
                                    onChange={e => setComments(e.target.value)}
                                    data-test-selector="comments"
                                />
                            </GridItem>
                            <GridItem {...styles.oneLinerGridItem}>
                                <StyledWrapper {...styles.submitButtonWrapper}>
                                    <Button
                                        {...styles.submitButton}
                                        onClick={() => submitButtonClickHandler(addToast)}
                                        data-test-selector="returnSubmit"
                                        disabled={inProgress}
                                    >
                                        {translate("Submit")}
                                    </Button>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                )}
            </ToasterContext.Consumer>
        </Toaster>
    );
};

export default connect(mapStateToProps)(BackOrderRequestExpediteForm);
